// Libs
import classNames from 'classnames/bind';
import React, { memo, useEffect } from 'react';
import type { Transform } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
// Components, Layouts, Pages
// Others
import { ColumnTypeKanban, IBaseKanban } from '~/utils/interface/common';
// Styles, images, icons
import styles from './ItemKanban.module.scss';

type Props<T extends IBaseKanban> = {
  dragOverlay?: boolean;
  disabled?: boolean;
  dragging?: boolean;
  handle?: boolean;
  handleProps?: any;
  height?: number;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  sorting?: boolean;
  style?: React.CSSProperties;
  transition?: string | null;
  data: T;
  columnData?: ColumnTypeKanban<T>;
  renderItem: (data: T, columnData?: ColumnTypeKanban<T>) => React.ReactNode;
};

const cx = classNames.bind(styles);

const ItemKanban = <T extends IBaseKanban>(props: Props<T>, ref: React.Ref<HTMLDivElement>) => {
  //#region Destructuring Props
  const {
    dragOverlay,
    dragging,
    disabled,
    fadeIn,
    handle,
    handleProps,
    height,
    index,
    listeners,
    renderItem,
    sorting,
    style,
    transition,
    transform,
    data,
    columnData,
    ...restProps
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!dragOverlay) {
      return;
    }

    document.body.style.cursor = 'grabbing';

    return () => {
      document.body.style.cursor = '';
    };
  }, [dragOverlay]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box
      ref={ref}
      component='div'
      className={cx(
        'itemKanbanWrapper',
        fadeIn && styles.fadeIn,
        sorting && styles.sorting,
        dragOverlay && styles.dragOverlay
      )}
      style={
        {
          transition: [transition].filter(Boolean).join(', '),
          '--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
          '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
          '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
          '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined,
          '--index': index,
        } as React.CSSProperties
      }
      {...restProps}
    >
      <Box
        {...listeners}
        className={cx(
          'items',
          dragging && styles.dragging,
          handle && styles.withHandle,
          dragOverlay && styles.dragOverlay,
          disabled && styles.disabled
        )}
      >
        {renderItem ? renderItem(data, columnData) : data.name}
      </Box>
    </Box>
  );
};

const ItemKanbanWithRef = React.forwardRef(ItemKanban) as <T extends IBaseKanban>(
  props: Props<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element;

export default memo(ItemKanbanWithRef);
